import "responsive-bp/build/responsive";
import "./utils/uiHelpers";

/**
 * Initialises a dynamically imported module if it has a default export function.
 * @param {Promise} modulePromise - The dynamic import promise
 */
const initModule = (modulePromise) => {
  modulePromise.then((module) => {
    if ("default" in module && typeof module.default === "function") {
      module.default();
    }
  });
};

/**
 * Conditionally loads a component if the selector exists in the DOM.
 * @param {string} selector - CSS selector to check
 * @param {function} loader - Function that returns the dynamic import promise
 */
const loadIfExists = (selector, loader) => {
  if (document.querySelector(selector)) {
    initModule(loader());
  }
};

/**
 * Conditionally loads a component if any of the selectors exist in the DOM.
 * @param {string[]} selectors - Array of CSS selectors to check
 * @param {function} loader - Function that returns the dynamic import promise
 */
const loadIfAnyExists = (selectors, loader) => {
  if (selectors.some((s) => document.querySelector(s))) {
    initModule(loader());
  }
};

// Dynamically load components based on DOM presence
// prettier-ignore
const components = [
  [".js-access-gate", () => import("./components/accessGate")],
  [".js-accordion", () => import("./components/accordion")],
  [".js-ajax-content-load", () => import("./components/ajaxContentLoad")],
  [".js-almanac-panel-carousel", () => import("./components/carousel/almanacPanelCarousel")],
  [".js-booking-dropdown", () => import("./components/bookingDropdown")],
  [".js-booking-root", () => import("./booking/index")],
  [".js-booking-widget", () => import("./booking/bookingWidget")],
  [".js-carousel-panel-carousel", () => import("./components/carousel/carouselPanelCarousel")],
  [".js-cta-panel-carousel", () => import("./components/carousel/ctaPanelCarousel")],
  [".js-distance-from-pig-hotel-list", () => import("./components/distanceFromPig")],
  [".js-event-tracking", () => import("./components/eventTracking")],
  [".js-experiences-datepicker", () => import("./components/experiences")],
  [".js-experiences-list-panel-carousel", () => import("./components/carousel/experiencesListPanelCarousel")],
  [".js-fancydropdown-trigger", () => import("./components/fancydropdown")],
  [".js-featured-experiences", () => import("./components/featuredExperiences")],
  [".js-gallery", () => import("./components/gallery")],
  [".js-grid-panel-carousel", () => import("./components/carousel/gridPanelCarousel")],
  [".js-hero-carousel", () => import("./components/carousel/heroCarousel")],
  [".js-image-gallery", () => import("./components/imageGallery")],
  [".js-listings-component", () => import("./components/listings/index")],
  [".js-load-more", () => import("./components/loadMore")],
  [".js-masonry-layout", () => import("./components/masonry")],
  [".js-menu-list-panel-carousel", () => import("./components/carousel/menuListPanelCarousel")],
  [".js-menu-list-filters", () => import("./components/menuListFilters")],
  [".js-nav", () => import("./components/nav")],
  [".js-notification-modal", () => import("./components/notificationModal")],
  [".js-offer-list-panel-carousel", () => import("./components/carousel/offerListPanelCarousel")],
  [".js-popup-trigger", () => import("./components/popup")],
  [".js-primary-header", () => import("./components/header")],
  [".js-related-restaurants-panel-carousel", () => import("./components/carousel/relatedRestaurantsPanelCarousel")],
  [".js-rooms-listings-datepicker", () => import("./components/roomsListingsDatepicker")],
  [".js-related-rooms-panel-carousel", () => import("./components/carousel/relatedRoomsPanelCarousel")],
  [".js-resource-download-all", () => import("./components/resourceDownload")],
  [".js-room-details-panel-carousel", () => import("./components/carousel/roomDetailsPanelCarousel")],
  [".js-room-explorer", () => import("./components/roomExplorer")],
  [".js-secondary-footer", () => import("./components/footerObserver")],
  [".js-slideshow-panel-carousel", () => import("./components/carousel/slideshowPanelCarousel")],
  [".js-smooth-scroll", () => import("./components/smoothScroll")],
  [".js-stacked-cards", () => import("./components/stackedCards")],
  [".js-stepper", () => import("./components/stepper")],
  [".js-table-booking-widget-trigger", () => import("./components/tableBookingWidget")],
  [".js-tabs", () => import("./components/tabs")],
  [".js-things-filter-group", () => import("./components/thingsToKnowFilter")],
  [".js-vimeo-player", () => import("./components/vimeoPlayer")],
  [".umbraco-forms-form", () => import("./utils/formOverrides")],
  ["div[data-primdo='True']", () => import("./utils/preventImageDownloads")],
  ["lite-vimeo", () => import("./utils/videoFacade")],
  [".js-faqs-component", () => import("./components/faqs")]
];

components.forEach(([selector, loader]) => loadIfExists(selector, loader));

// Multi-selector components
loadIfAnyExists(
  [
    ".js-carousel-availability",
    ".js-late-availability-calendar",
    ".js-late-availability-carousel",
  ],
  () => import("./components/availabilityPanels")
);
loadIfAnyExists(
  [".js-carousel-locations", ".js-map-carousel-locations", ".js-carousel--with-fades-and-arrows"],
  () => import("./components/carousel/carouselLegacy")
);
loadIfAnyExists(
  [".js-location-list", ".js-map"],
  () => import("./components/locations/index")
);
